/**
 * Quotes API client (Phase 1 Steps 3–4).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 * Do NOT set X-Api-Token from the browser.
 */

import type {
  PriceRule,
  QuoteStatus,
  SupplierQuote,
  VatRateStatus,
} from './types'
import type { NormalizedImportDTO } from './import/types'
import { envelopeToCommitPayload } from './intake/mapToCommit'
import { normalizeIntakeEnvelope } from './intake/normalize'
import { normalizedImportToEnvelope } from './intake/fromNormalizedImport'

export type QuotesApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class QuotesApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'QuotesApiClientError'
    this.code = code
    this.status = status
  }
}

type QuoteApiDto = {
  id: string
  supplier_id: string
  title: string
  status: string
  source_type: string
  source_file_id: string | null
  attachment_file_id?: string | null
  template_id: string | null
  currency: string
  vat_rate_status: VatRateStatus
  vat_rate_value: number | null
  import_content_signature: string | null
  quote_date: string | null
  archived_at?: string | null
}

type RuleApiDto = {
  id: string
  quote_id: string
  supplier_id: string
  item_id: string | null
  supplier_item_name: string
  status: string
  uom: string
  unit_price: number
  currency: string
  vat_rate_status: VatRateStatus
  vat_rate_value: number | null
  original_row_ref: string | null
  spec_attributes?: Record<string, unknown>
  original_values?: Record<string, unknown>
}

export type IntakeCommitSuccessBody = {
  ok: true
  idempotent_replay: boolean
  duplicate_action: string
  skipped?: boolean
  candidates?: Array<{ quote_id: string; match_reasons: string[] }>
  quote: QuoteApiDto | null
  rules: RuleApiDto[]
  import_content_signature: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as QuotesApiError
  throw new QuotesApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

/**
 * Step 1 read DTO is the intake subset. Map to UI SupplierQuote with safe
 * defaults for fields not yet returned by GET /api/quotes.
 */
export function quoteApiDtoToUi(dto: QuoteApiDto): SupplierQuote {
  return {
    id: dto.id,
    supplier_id: dto.supplier_id,
    title: dto.title,
    quote_date: dto.quote_date,
    status: dto.status as QuoteStatus,
    product_family: null,
    currency: dto.currency,
    vat_rate_status: dto.vat_rate_status,
    vat_rate_value: dto.vat_rate_value,
    effective_from: null,
    effective_until: null,
    confirmation_deadline_hours: null,
    commercial_terms: [],
    warnings: [],
    import_content_signature: dto.import_content_signature ?? undefined,
    attachment_file_id: dto.attachment_file_id ?? null,
    archived_at: dto.archived_at ?? null,
    created_at: '',
    updated_at: '',
  }
}

export function ruleApiDtoToUi(dto: RuleApiDto): PriceRule {
  return {
    id: dto.id,
    quote_id: dto.quote_id,
    supplier_id: dto.supplier_id,
    item_id: dto.item_id,
    supplier_item_name: dto.supplier_item_name,
    status: dto.status as PriceRule['status'],
    uom: dto.uom,
    unit_price: dto.unit_price,
    currency: dto.currency,
    vat_rate_status: dto.vat_rate_status,
    vat_rate_value: dto.vat_rate_value,
    spec_attributes:
      dto.spec_attributes && typeof dto.spec_attributes === 'object'
        ? dto.spec_attributes
        : {},
    original_values:
      dto.original_values && typeof dto.original_values === 'object'
        ? dto.original_values
        : {},
    original_row_ref: dto.original_row_ref ?? '',
    warnings: [],
  }
}

export type ListQuotesApiFilter = {
  supplier_id?: string
  include_archived?: boolean
}

export async function listQuotesApi(
  filter?: ListQuotesApiFilter,
): Promise<SupplierQuote[]> {
  const params = new URLSearchParams()
  if (filter?.supplier_id) params.set('supplier_id', filter.supplier_id)
  if (filter?.include_archived) params.set('include_archived', 'true')
  const queryStr = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`/api/quotes${queryStr}`)
  const body = await parseJson<{ ok: true; quotes: QuoteApiDto[] } | QuotesApiError>(
    res,
  )
  throwIfApiError(res, body, 'Failed to list quotes')
  const quotes = (body as { quotes: QuoteApiDto[] }).quotes.map(quoteApiDtoToUi)
  if (filter?.supplier_id) {
    return quotes.filter((q) => q.supplier_id === filter.supplier_id)
  }
  return quotes
}

export async function archiveQuoteApi(quoteId: string): Promise<SupplierQuote> {
  const res = await fetch(`/api/quotes/${encodeURIComponent(quoteId)}/archive`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: true; quote: QuoteApiDto } | QuotesApiError>(res)
  throwIfApiError(res, body, 'Lưu trữ báo giá thất bại')
  return quoteApiDtoToUi((body as { quote: QuoteApiDto }).quote)
}

export async function restoreQuoteApi(quoteId: string): Promise<SupplierQuote> {
  const res = await fetch(`/api/quotes/${encodeURIComponent(quoteId)}/restore`, {
    method: 'POST',
  })
  const body = await parseJson<{ ok: true; quote: QuoteApiDto } | QuotesApiError>(res)
  throwIfApiError(res, body, 'Khôi phục báo giá thất bại')
  return quoteApiDtoToUi((body as { quote: QuoteApiDto }).quote)
}

export async function getQuoteApi(id: string): Promise<SupplierQuote | null> {
  const res = await fetch(`/api/quotes/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: true; quote: QuoteApiDto } | QuotesApiError>(
    res,
  )
  throwIfApiError(res, body, 'Failed to get quote')
  return quoteApiDtoToUi((body as { quote: QuoteApiDto }).quote)
}

export async function listRulesByQuoteApi(quoteId: string): Promise<PriceRule[]> {
  const res = await fetch(
    `/api/quotes/${encodeURIComponent(quoteId)}/rules`,
  )
  const body = await parseJson<{ ok: true; rules: RuleApiDto[] } | QuotesApiError>(
    res,
  )
  throwIfApiError(res, body, 'Failed to list quote rules')
  return (body as { rules: RuleApiDto[] }).rules.map(ruleApiDtoToUi)
}

/** PATCH /api/quotes/:id/rules/:ruleId — NEEDS_MAPPING → PENDING_ACTIVATION */
export async function mapRuleItemApi(
  quoteId: string,
  ruleId: string,
  itemId: string,
): Promise<PriceRule> {
  const res = await fetch(
    `/api/quotes/${encodeURIComponent(quoteId)}/rules/${encodeURIComponent(ruleId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_id: itemId }),
    },
  )
  const body = await parseJson<{ ok: true; rule: RuleApiDto } | QuotesApiError>(
    res,
  )
  throwIfApiError(res, body, 'Map rule thất bại')
  return ruleApiDtoToUi((body as { rule: RuleApiDto }).rule)
}

/** POST /api/quotes/:id/activate */
export async function activateQuoteApi(
  quoteId: string,
): Promise<{ quote: SupplierQuote; rules: PriceRule[] }> {
  const res = await fetch(
    `/api/quotes/${encodeURIComponent(quoteId)}/activate`,
    { method: 'POST' },
  )
  const body = await parseJson<
    | { ok: true; quote: QuoteApiDto; rules: RuleApiDto[] }
    | QuotesApiError
  >(res)
  throwIfApiError(res, body, 'Activate quote thất bại')
  const ok = body as { quote: QuoteApiDto; rules: RuleApiDto[] }
  return {
    quote: quoteApiDtoToUi(ok.quote),
    rules: ok.rules.map(ruleApiDtoToUi),
  }
}

// --- Intake upload / commit ---

export type SourceFileUploadResult = {
  ok: true
  source_file_id: string
  original_file_name: string
  content_type: string | null
  size_bytes: number
  sha256: string
  uploaded_at: string
}

export type IntakeApiError = QuotesApiError

/**
 * Map F1 NormalizedImportDTO → G-C IntakeCommitRequestV1 via D4 Shared Adapter.
 * Preserves Phase 1 wizard label STANDARD_EXCEL until D2 reclassifies free-map.
 * Server requires non-empty uom — MISSING_UOM blocks (no invented unit).
 */
export function normalizedImportToIntakePayload(
  dto: NormalizedImportDTO,
): Record<string, unknown> {
  try {
    const envelope = normalizeIntakeEnvelope(
      normalizedImportToEnvelope(dto, { source_type: 'STANDARD_EXCEL' }),
    )
    return envelopeToCommitPayload(envelope) as unknown as Record<string, unknown>
  } catch (err) {
    if (
      err &&
      typeof err === 'object' &&
      'code' in err &&
      typeof (err as { code: unknown }).code === 'string'
    ) {
      const e = err as { code: string; message: string }
      throw new QuotesApiClientError(e.code, e.message, 400)
    }
    throw err
  }
}

export async function uploadIntakeSourceFile(
  file: File,
): Promise<SourceFileUploadResult> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/intake/source-files', {
    method: 'POST',
    body: form,
  })
  const body = await parseJson<SourceFileUploadResult | IntakeApiError>(res)
  if (!res.ok || !('ok' in body) || body.ok !== true) {
    const err = body as IntakeApiError
    throw new QuotesApiClientError(
      err.error?.code ?? 'UPLOAD_FAILED',
      err.error?.message ?? `Upload failed (${res.status})`,
      err.error?.status ?? res.status,
    )
  }
  return body
}

/** Attach uploaded source file as quote reference attachment (not intake source_file_id). */
export async function attachQuoteAttachmentApi(
  quoteId: string,
  fileId: string,
): Promise<SupplierQuote> {
  const res = await fetch(
    `/api/quotes/${encodeURIComponent(quoteId)}/attachment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_id: fileId }),
    },
  )
  const body = await parseJson<
    { ok: true; quote: QuoteApiDto } | QuotesApiError
  >(res)
  throwIfApiError(res, body, 'Failed to attach quote file')
  return quoteApiDtoToUi((body as { quote: QuoteApiDto }).quote)
}

export function quoteAttachmentDownloadUrl(quoteId: string): string {
  return `/api/quotes/${encodeURIComponent(quoteId)}/attachment`
}

export async function commitIntake(
  payload: Record<string, unknown>,
  opts: { idempotencyKey: string },
): Promise<{ status: number; body: unknown }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Idempotency-Key': opts.idempotencyKey,
  }
  const res = await fetch('/api/intake/commit', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })
  const body = await parseJson<unknown>(res)
  return { status: res.status, body }
}

/**
 * Wizard helper: map NormalizedImportDTO → intake commit → UI quote/rules.
 */
export async function commitNormalizedImportApi(
  dto: NormalizedImportDTO,
  opts?: { idempotencyKey?: string },
): Promise<{ quote: SupplierQuote; rules: PriceRule[] }> {
  const payload = normalizedImportToIntakePayload(dto)
  const idempotencyKey = opts?.idempotencyKey ?? crypto.randomUUID()
  const { status, body } = await commitIntake(payload, { idempotencyKey })

  if (status === 200 || status === 201) {
    const ok = body as IntakeCommitSuccessBody
    if (ok.skipped) {
      throw new QuotesApiClientError(
        'DUPLICATE_SKIPPED',
        'Import bị SKIP theo chính sách trùng lặp — không tạo Quote.',
        status,
      )
    }
    if (!ok.quote) {
      throw new QuotesApiClientError(
        'NO_QUOTE',
        'Commit thành công nhưng không có quote trong response.',
        status,
      )
    }
    return {
      quote: quoteApiDtoToUi(ok.quote),
      rules: (ok.rules ?? []).map(ruleApiDtoToUi),
    }
  }

  const err = body as QuotesApiError
  throw new QuotesApiClientError(
    err.error?.code ?? 'COMMIT_FAILED',
    err.error?.message ?? `Confirm thất bại (${status})`,
    err.error?.status ?? status,
  )
}

/**
 * Link or unlink a price rule with a Material Master (item_id).
 */
export async function updateRuleMaterialApi(
  quoteId: string,
  ruleId: string,
  itemId: string | null,
): Promise<{ rule: PriceRule }> {
  const res = await fetch(`/api/quotes/${quoteId}/rules/${ruleId}/material`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ item_id: itemId }),
  })
  const body = await parseJson<any>(res)
  throwIfApiError(res, body, 'Cập nhật liên kết vật tư thất bại')
  return { rule: ruleApiDtoToUi(body.rule) }
}
